import { useEffect, useState } from "react";
import { useCompany } from "../context/CompanyContext";

/**
 * PWA install button — only rendered when the browser fires the
 * `beforeinstallprompt` event (Chrome/Edge on desktop/Android).
 * iOS Safari uses the "Add to Home Screen" menu instead, so we show
 * a subtle hint there too.
 *
 * Visual: small LuxStay logo + "LuxStay" wordmark, so the user recognises
 * exactly what will land on their home screen.
 */
export default function InstallPWAButton({ variant = "inline" }) {
  const { company, logoDark } = useCompany();
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    const installed = (() => {
      if (window.matchMedia?.("(display-mode: standalone)").matches) return true;
      return window.navigator.standalone === true;
    })();
    if (installed) return;
    window.addEventListener("beforeinstallprompt", handler);
    // iOS Safari heuristic
    const ua = navigator.userAgent;
    if (/iPhone|iPad|iPod/.test(ua) && /Safari/.test(ua) && !/CriOS|FxiOS/.test(ua)) {
      setIosHint(true);
    }
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  async function install() {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch { /* ignore */ }
    setDeferred(null);
    setVisible(false);
  }

  if (!visible && !iosHint) return null;

  if (variant === "banner") {
    return (
      <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-80 z-40 bg-[#062c27] border border-[#d4af37] rounded-sm p-4 shadow-2xl" data-testid="pwa-banner">
        <div className="flex items-start gap-3">
          <span className="bg-[#fdfbf7] rounded-sm p-1.5 inline-flex items-center flex-shrink-0 mt-0.5">
            <img src={logoDark} alt={company.trade_name} className="h-10 w-auto" />
          </span>
          <div className="flex-1">
            <div className="text-sm font-medium">Adicionar <span className="text-[#d4af37]">{company.short_name}</span> ao ecrã principal</div>
            <div className="text-[11px] text-[#a0b2af] mt-1">
              {iosHint
                ? "Toque no botão de partilha do Safari e escolha \"Adicionar ao Ecrã Principal\"."
                : "Abre em 1 toque · funciona offline · integração com GPS do telemóvel."}
            </div>
            {!iosHint && (
              <button onClick={install} className="btn-gold px-3 py-1.5 text-xs uppercase tracking-widest mt-3" data-testid="pwa-install-btn">
                Instalar agora
              </button>
            )}
          </div>
          <button onClick={() => { setVisible(false); setIosHint(false); }}
                  className="text-[#a0b2af] hover:text-[#fdfbf7] text-sm" aria-label="Fechar">✕</button>
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={install}
      disabled={!deferred}
      className="inline-flex items-center gap-2 px-2.5 py-1.5 border border-[#1a4640] rounded-sm text-[#a0b2af] hover:border-[#d4af37] hover:text-[#d4af37] transition-colors"
      data-testid="pwa-install-btn"
      title={`Adicionar ${company.short_name} ao ecrã principal`}
      aria-label={`Adicionar ${company.short_name} ao ecrã principal`}
    >
      <img src="/assets/luxmadeira-icon-192.png" alt="" aria-hidden="true" className="h-4 w-auto" />
      <span className="text-xs uppercase tracking-widest">{company.short_name}</span>
    </button>
  );
}
